<template>
  <div class="wrapper">
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Staff claims</h1>
            <p class="text-muted mb-0">Review, approve and pay back what your staff spent for work</p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add claim
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Filters -->
    <CCard v-if="items.length" class="mb-4 border">
      <CCardBody class="pb-0">
        <CRow>
          <CCol md="5">
            <CInput label="Search" v-model="search" placeholder="Employee or claim no." />
          </CCol>
          <CCol md="3">
            <CSelect label="Status" :value.sync="statusFilter" :options="statusOptions" />
          </CCol>
          <CCol md="4" class="d-flex align-items-center justify-content-md-end mb-3">
            <span class="text-muted small mr-3">{{ filteredItems.length }} of {{ items.length }} claims</span>
            <CButton v-if="hasActiveFilters" color="light" size="sm" @click="clearFilters">
              <CIcon name="cil-x" /> Clear filters
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard class="border-0">
      <!-- First load -->
      <CCardBody v-if="loading && !items.length" class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading claims…</p>
      </CCardBody>

      <!-- Error -->
      <CCardBody v-else-if="loadError && !items.length" class="text-center py-5">
        <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
        <h2 class="h5">Couldn't load claims</h2>
        <p class="text-muted mb-3">Check your connection and try again.</p>
        <CButton color="primary" @click="refreshTable">
          <CIcon name="cil-reload" /> Try again
        </CButton>
      </CCardBody>

      <!-- Empty -->
      <CCardBody v-else-if="!items.length" class="text-center py-5">
        <CIcon name="cil-description" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No claims yet</h2>
        <p class="text-muted mb-0">Claims your staff submit for approval appear here.</p>
      </CCardBody>

      <!-- Filtered empty -->
      <CCardBody v-else-if="!filteredItems.length" class="text-center py-5">
        <CIcon name="cil-filter" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No claims match your filters</h2>
        <CButton color="light" class="mt-2" @click="clearFilters">
          <CIcon name="cil-x" /> Clear filters
        </CButton>
      </CCardBody>

      <template v-else>
        <CProgress v-if="loading" :value="100" animated color="primary" size="xs" />
        <!-- Desktop table -->
        <CCardBody class="d-none d-lg-block">
          <CDataTable
            :items="filteredItems"
            :fields="fields"
            items-per-page-select
            :items-per-page="25"
            hover
            sorter
            :sorter-value="{ column: 'date', asc: false }"
            pagination
            responsive
          >
            <template #claimNo="{ item }">
              <td class="py-2 text-monospace">
                <router-link :to="`/tenants/Claim/${item.id}`">{{ item.claimNo || "—" }}</router-link>
              </td>
            </template>
            <template #date="{ item }">
              <td class="py-2">{{ formatDate(item.date) }}</td>
            </template>
            <template #totalAmount="{ item }">
              <td class="py-2 text-right font-weight-bold">{{ money(item.totalAmount) }}</td>
            </template>
            <template #claimStateDescription="{ item }">
              <td class="py-2">
                <CBadge :color="hrStatusColor('claim', item.claimStateDescription)">
                  {{ hrStatusLabel('claim', item.claimStateDescription) }}
                </CBadge>
              </td>
            </template>
            <template #actions="{ item }">
              <td class="py-2 text-right text-nowrap">
                <CButton color="light" size="sm" class="border" :aria-label="`Open claim ${item.claimNo || ''}`" title="Open" @click="onEdit(item)">
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton color="light" size="sm" class="border text-danger ml-1" :aria-label="`Delete claim ${item.claimNo || ''}`" title="Delete" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" />
                </CButton>
              </td>
            </template>
          </CDataTable>
        </CCardBody>

        <!-- Phone / tablet cards -->
        <CCardBody class="d-lg-none p-2">
          <div v-for="item in filteredItems" :key="item.id" class="border rounded p-3 mb-2">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <router-link :to="`/tenants/Claim/${item.id}`" class="font-weight-bold">{{ item.profileName }}</router-link>
                <small class="d-block text-muted">
                  <span class="text-monospace">{{ item.claimNo }}</span> · {{ formatDate(item.date) }}
                </small>
              </div>
              <CBadge :color="hrStatusColor('claim', item.claimStateDescription)">
                {{ hrStatusLabel('claim', item.claimStateDescription) }}
              </CBadge>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-2">
              <span class="font-weight-bold">{{ money(item.totalAmount) }}</span>
              <CDropdown color="light" placement="bottom-end">
                <template #toggler-content>
                  <CIcon name="cil-options" />
                  <span class="sr-only">More actions</span>
                </template>
                <CDropdownItem @click="onEdit(item)">
                  <CIcon name="cil-pencil" class="mr-2" /> Open
                </CDropdownItem>
                <CDropdownDivider />
                <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" class="mr-2" /> Delete
                </CDropdownItem>
              </CDropdown>
            </div>
          </div>
        </CCardBody>
      </template>
    </CCard>

    <CModal
      :title="`Delete claim ${itemToDelete.claimNo || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The claim from <strong>{{ itemToDelete.profileName }}</strong>
        dated {{ formatDate(itemToDelete.date) }} for {{ money(itemToDelete.totalAmount) }}
        will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" /> Delete claim
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ClaimApi from "@/lib/claimApi";
import { formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { hrStatusMixin, hrStatusOptions } from "@/utils/hrStatusBadges";

const fields = [
  { key: "claimNo", label: "Claim no." },
  { key: "profileName", label: "Employee" },
  { key: "date", label: "Date" },
  { key: "totalAmount", label: "Amount", _style: "text-align:right" },
  { key: "claimStateDescription", label: "Status" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "ClaimList",
  mixins: [hrStatusMixin],
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      fields,
      api: new ClaimApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
      search: "",
      statusFilter: "",
      statusOptions: hrStatusOptions("claim"),
    };
  },
  computed: {
    rows() {
      return this.items.map((item) => ({
        ...item,
        profileName: (item.profile && item.profile.fullName) || "",
        businessName: (item.business && item.business.name) || "",
        totalAmount: (item.items || []).reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0),
      }));
    },
    hasActiveFilters() {
      return !!(this.search || this.statusFilter);
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      return this.rows.filter((item) => {
        if (this.statusFilter && item.claimStateDescription !== this.statusFilter) return false;
        if (!q) return true;
        return (
          item.profileName.toLowerCase().includes(q) ||
          (item.claimNo || "").toLowerCase().includes(q)
        );
      });
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Staff claims"]);
    this.refreshTable();
  },
  methods: {
    formatDate,
    money(v) {
      return `RM ${formatMyr(v)}`;
    },
    clearFilters() {
      this.search = "";
      this.statusFilter = "";
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentUser()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error("Couldn't load claims", err);
          this.loadError = true;
          if (this.items.length) {
            this.$toast.error("Couldn't refresh claims. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Claim/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Claim deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't delete the claim. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/employee/Claim" });
    },
  },
};
</script>
